// Знания агента (PLAN «Память и знания»): слово владельца и планы (решение владельца 05.10.2026 — агент помнит
// сказанное), правила методики, выводы, гипотезы со сроком жизни, итоги дня/недели/месяца, архив. Поиск — по смыслу.
// Правила утверждает владелец; он же добавляет, правит и убирает любую заметку. Слово владельца, планы, правила и
// гипотезы агент видит в «Памятке» в начале каждого разговора и работы.
import { Button, Chip, dateShort, EmptyState, pct, Segmented, toast, usd, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, BookOpen, Check, Pencil, Plus, RotateCcw, Search, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import type { DigestView, NoteEvent, NoteView } from '../../agent-api';
import { api } from '../../api';
import { SidePanel } from '../../shell/SidePanel';
import { Md } from './Feed';

type View = 'owner' | 'plan' | 'rule' | 'insight' | 'hypothesis' | 'digests' | 'archive';

const KIND: Record<NoteView['kind'], string> = {
  owner: 'Слово владельца',
  plan: 'План',
  rule: 'Правило',
  insight: 'Вывод',
  hypothesis: 'Гипотеза',
};

const EVENT: Record<string, string> = {
  created: 'Записана',
  confirmed: 'Подтверждена',
  refuted: 'Опровергнута',
  updated: 'Изменена',
  merged: 'Слита с другими',
  archived: 'В архиве',
  restored: 'Возвращена',
  expired: 'Погасла: срок вышел без подтверждений',
  promoted: 'Стала правилом методики',
  done: 'Сделано',
  cancelled: 'Отменено',
};
const ACTOR: Record<string, string> = { agent: 'агент', owner: 'вы', platform: 'платформа' };

/** Слово владельца и план — не про данные: без уверенности и подтверждений. */
const standing = (n: NoteView) => n.kind === 'owner' || n.kind === 'plan';
const overdue = (n: NoteView) =>
  n.kind === 'plan' && n.status === 'active' && n.dueAt !== null && new Date(n.dueAt).getTime() < Date.now();

const ARCHIVED: Record<string, string> = { expired: 'погасла', done: 'сделано', cancelled: 'отменено' };

function NoteMeta({ n }: { n: NoteView }) {
  return (
    <div className="note-meta">
      {n.kind === 'plan' &&
        n.status === 'active' &&
        (overdue(n) ? (
          <Chip tone="danger">просрочен: срок был {dateShort(n.dueAt as string)}</Chip>
        ) : (
          <span>{n.dueAt ? `срок — ${dateShort(n.dueAt)}` : 'без срока'}</span>
        ))}
      {!standing(n) && n.kind !== 'rule' && <span>уверенность {pct(n.confidence * 100, 0)}</span>}
      {n.confirmedCount > 0 && <span>подтверждений {n.confirmedCount}</span>}
      {n.refutedCount > 0 && <span className="neg">опровержений {n.refutedCount}</span>}
      {n.kind === 'hypothesis' && n.status === 'active' && n.expiresAt && (
        <span>гаснет {when(n.expiresAt)}</span>
      )}
      {n.status === 'archived' && (
        <span>
          {n.archivedReason && ARCHIVED[n.archivedReason]
            ? ARCHIVED[n.archivedReason]
            : `в архиве: ${n.archivedReason ?? ''}`}
        </span>
      )}
      {n.scope &&
        Object.values(n.scope)
          .filter(Boolean)
          .map((v) => <Chip key={String(v)}>{String(v)}</Chip>)}
      {n.createdBy === 'owner' ? (
        <Chip tone="info">от вас</Chip>
      ) : (
        standing(n) && <span>записал агент {dateShort(n.createdAt)}</span>
      )}
    </div>
  );
}

/** Дата плана для поля «срок»: ГГГГ-ММ-ДД по Ташкенту. */
const dueInput = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' }) : '';

function NotePanel({
  id,
  onClose,
  onChanged,
}: {
  id: number | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useQuery({
    queryKey: ['agent', 'note', id],
    queryFn: () => api<NoteView & { history: NoteEvent[] }>(`/api/agent/notes/${id}`),
    enabled: id !== null,
  });
  const [edit, setEdit] = useState<{ text: string; due: string } | null>(null);
  // Открыли другую заметку — правка прошлой не переносится
  const [editFor, setEditFor] = useState(id);
  if (editFor !== id) {
    setEditFor(id);
    setEdit(null);
  }
  const n = q.data;
  const act = async (path: string, body?: unknown, method?: string, done?: string) => {
    try {
      await api(`/api/agent/notes/${id}${path}`, body ?? {}, method);
      if (done) toast(done);
      setEdit(null);
      void q.refetch();
      onChanged();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const isPlan = n?.kind === 'plan';
  return (
    <SidePanel
      open={id !== null}
      label="Заметка агента"
      onClose={onClose}
      head={
        <div className="sp-title">
          <b>{n ? `${KIND[n.kind]} #${n.id}` : 'Заметка'}</b>
          <span>{n ? `записана ${when(n.createdAt)}` : ''}</span>
        </div>
      }
      footer={
        n && (
          <div className="flex gap-2 flex-wrap">
            {edit === null && n.status !== 'merged' && (
              <Button icon={<Pencil />} onClick={() => setEdit({ text: n.text, due: dueInput(n.dueAt) })}>
                Изменить
              </Button>
            )}
            {isPlan && n.status === 'active' && (
              <Button
                variant="ghost"
                icon={<Check />}
                onClick={() => void act('/close', { outcome: 'done' }, undefined, 'План отмечен сделанным')}
              >
                Сделано
              </Button>
            )}
            {isPlan && n.status === 'active' && (
              <Button
                variant="ghost"
                icon={<X />}
                onClick={() => void act('/close', { outcome: 'cancelled' }, undefined, 'План отменён')}
              >
                Отменить план
              </Button>
            )}
            {!isPlan && n.status === 'active' && (
              <Button
                variant="ghost"
                icon={<Archive />}
                onClick={() =>
                  void act('/archive', {}, undefined, 'Заметка в архиве — агент её больше не держит')
                }
              >
                В архив
              </Button>
            )}
            {n.status === 'archived' && (
              <Button
                variant="ghost"
                icon={<RotateCcw />}
                onClick={() => void act('/restore', {}, undefined, 'Заметка снова активна')}
              >
                Вернуть
              </Button>
            )}
          </div>
        )
      }
    >
      {!n ? (
        <div className="skeleton" style={{ height: 120 }} />
      ) : (
        <>
          <section className="sp-sec">
            {edit === null ? (
              <p className="note-text">{n.text}</p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void act(
                    '',
                    { text: edit.text, ...(isPlan ? { due: edit.due || null } : {}) },
                    'PATCH',
                    'Сохранено',
                  );
                }}
                className="flex flex-col gap-2"
              >
                <textarea
                  className="input"
                  rows={5}
                  value={edit.text}
                  onChange={(e) => setEdit({ ...edit, text: e.target.value })}
                  aria-label="Текст заметки"
                />
                {isPlan && (
                  <label className="kn-due">
                    <span>Срок</span>
                    <input
                      className="input"
                      type="date"
                      value={edit.due}
                      onChange={(e) => setEdit({ ...edit, due: e.target.value })}
                    />
                  </label>
                )}
                <div className="flex gap-2">
                  <Button type="submit" variant="primary">
                    Сохранить
                  </Button>
                  <Button variant="ghost" onClick={() => setEdit(null)}>
                    Отмена
                  </Button>
                </div>
              </form>
            )}
            <NoteMeta n={n} />
          </section>
          {n.evidence.length > 0 && (
            <section className="sp-sec">
              <h3>{n.kind === 'owner' ? 'Ваши слова' : 'Доказательства'}</h3>
              <ul className="note-ev">
                {n.evidence.map((e) => (
                  <li key={`${e.at}${e.text}`}>
                    <p>{e.text}</p>
                    <small>{when(e.at)}</small>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="sp-sec">
            <h3>История</h3>
            <ol className="timeline">
              {n.history.map((h) => {
                const detail =
                  typeof h.data?.evidence === 'string'
                    ? h.data.evidence
                    : typeof h.data?.reason === 'string'
                      ? h.data.reason
                      : null;
                return (
                  <li
                    key={h.id}
                    data-tone={
                      h.type === 'confirmed' || h.type === 'promoted' || h.type === 'done'
                        ? 'success'
                        : h.type === 'refuted' || h.type === 'expired'
                          ? 'danger'
                          : undefined
                    }
                  >
                    <span className="tl-dot" />
                    <div>
                      <b>{EVENT[h.type] ?? h.type}</b>
                      {detail && <span>{detail}</span>}
                      <small>
                        {ACTOR[h.actor] ?? h.actor} · {when(h.createdAt)}
                      </small>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        </>
      )}
    </SidePanel>
  );
}

/** Что можно добавить самому: слово, план (со сроком), правило методики. */
const ADD: Partial<
  Record<View, { kind: NoteView['kind']; placeholder: string; label: string; button: string; done: string }>
> = {
  owner: {
    kind: 'owner',
    placeholder: 'Например: «По выходным бюджеты не поднимать»',
    label: 'Ваше решение или договорённость',
    button: 'Добавить',
    done: 'Записано — агент учтёт это в каждом новом разговоре и работе',
  },
  plan: {
    kind: 'plan',
    placeholder: 'Например: «Запустить тест турки на свой лендинг»',
    label: 'Новый план',
    button: 'Добавить план',
    done: 'План добавлен — агент увидит его в каждом новом разговоре и работе',
  },
  rule: {
    kind: 'rule',
    placeholder: 'Новое правило: «Никогда не упоминать возвраты в креативах»',
    label: 'Новое правило методики',
    button: 'Добавить правило',
    done: 'Правило добавлено — агент увидит его в каждом новом разговоре и работе',
  },
};

function AddNote({ view, onDone }: { view: View; onDone: () => void }) {
  const cfg = ADD[view];
  const [text, setText] = useState('');
  const [due, setDue] = useState('');
  if (!cfg) return null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api('/api/agent/notes', {
        kind: cfg.kind,
        text: text.trim(),
        ...(cfg.kind === 'plan' && due ? { due } : {}),
      });
      setText('');
      setDue('');
      toast(cfg.done);
      onDone();
    } catch (err) {
      toast((err as Error).message);
    }
  };
  return (
    <form className="kn-add" onSubmit={submit}>
      <input
        className="input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={cfg.placeholder}
        aria-label={cfg.label}
        maxLength={1500}
      />
      {cfg.kind === 'plan' && (
        <label className="kn-due">
          <span>Срок</span>
          <input
            className="input"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            aria-label="Срок плана (необязательно)"
          />
        </label>
      )}
      <Button type="submit" icon={<Plus />} disabled={text.trim().length < 8}>
        {cfg.button}
      </Button>
    </form>
  );
}

const PERIOD: Record<DigestView['period'], string> = { day: 'День', week: 'Неделя', month: 'Месяц' };

function Digests() {
  const q = useQuery({
    queryKey: ['agent', 'digests'],
    queryFn: () => api<DigestView[]>('/api/agent/digests?limit=40'),
  });
  if (q.isPending) return <div className="card skeleton" style={{ height: 200 }} />;
  if (!q.data?.length)
    return (
      <div className="card">
        <EmptyState icon={<BookOpen />} title="Итогов пока нет">
          Итог дня агент пишет в 21:00, итог недели — в понедельник из дневных, итог месяца — первого числа из
          недельных. Текст пишет агент, цифры подставляет платформа.
        </EmptyState>
      </div>
    );
  return (
    <>
      {q.data.map((d) => (
        <article key={d.id} className="card digest">
          <header className="feed-head">
            <Chip tone={d.period === 'day' ? 'default' : 'accent'}>{PERIOD[d.period]}</Chip>
            <b>
              {new Date(`${d.periodStart}T00:00:00Z`).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                timeZone: 'UTC',
              })}
              {d.numbers && d.numbers.until !== d.periodStart
                ? ` — ${new Date(`${d.numbers.until}T00:00:00Z`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' })}`
                : ''}
            </b>
          </header>
          {d.numbers && (
            <p className="digest-nums">
              <span>расход {usd(d.numbers.spend)}</span>
              <span>лиды {d.numbers.leads}</span>
              <span>апрувы {d.numbers.approved}</span>
              <span className={d.numbers.profit >= 0 ? 'pos' : 'neg'}>прибыль {usd(d.numbers.profit)}</span>
              <span>прогноз {usd(d.numbers.forecastProfit)}</span>
            </p>
          )}
          <Md text={d.text} />
        </article>
      ))}
    </>
  );
}

const EMPTY: Record<Exclude<View, 'digests'>, string> = {
  owner:
    'Здесь — ваши решения, предпочтения и договорённости: «льём только на свой лендинг», «турка стоит 199 000». Агент записывает их сам, как только вы скажете, и учитывает в каждом разговоре и работе. Можно добавить и самому.',
  plan: 'Планы — что договорились сделать или проверить, со сроком. Агент ведёт их до конца и отмечает сделанное; просроченные видны сразу.',
  rule: 'Правила методики — то, что агент соблюдает всегда. Их добавляете вы или утверждаете предложения агента, когда вывод подтвердился много раз.',
  insight: 'Выводы агент записывает сам: что сработало, с уверенностью и доказательствами.',
  hypothesis:
    'Гипотезы агент проверяет на данных. Не подтвердилась за срок — гаснет и уходит в архив; каждое подтверждение продлевает её.',
  archive: 'Сюда уходят погасшие гипотезы, закрытые планы и устаревшие заметки. Архив не удаляется.',
};

export function KnowledgeTab() {
  const qc = useQueryClient();
  const [view, setView] = useState<View>('owner');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const stats = useQuery({
    queryKey: ['agent', 'notes', 'stats'],
    queryFn: () => api<Array<{ kind: string; status: string; n: number }>>('/api/agent/notes/stats'),
  });
  const count = (kind: string | null, status: string) =>
    (stats.data ?? [])
      .filter((r) => (kind ? r.kind === kind : true) && r.status === status)
      .reduce((t, r) => t + r.n, 0);
  const params =
    view === 'archive' ? 'status=archived' : view === 'digests' ? '' : `kind=${view}&status=active`;
  const q = useQuery({
    queryKey: ['agent', 'notes', view, query],
    queryFn: () =>
      api<NoteView[]>(`/api/agent/notes?${params}${query ? `&q=${encodeURIComponent(query)}` : ''}`),
    enabled: view !== 'digests',
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['agent', 'notes'] });
  return (
    <div className="pc pc-read">
      <div className="kn-top">
        <div className="kn-tabs">
          <Segmented
            label="Раздел знаний"
            value={view}
            onChange={(v) => {
              setView(v);
              setQuery('');
              setSearch('');
            }}
            options={[
              { value: 'owner', label: `Слово владельца · ${count('owner', 'active')}` },
              { value: 'plan', label: `Планы · ${count('plan', 'active')}` },
              { value: 'rule', label: `Правила · ${count('rule', 'active')}` },
              { value: 'insight', label: `Выводы · ${count('insight', 'active')}` },
              { value: 'hypothesis', label: `Гипотезы · ${count('hypothesis', 'active')}` },
              { value: 'digests', label: 'Итоги' },
              { value: 'archive', label: `Архив · ${count(null, 'archived')}` },
            ]}
          />
        </div>
        {view !== 'digests' && (
          <form
            className="search-field kn-search"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(search.trim());
            }}
          >
            <Search size={16} />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) setQuery('');
              }}
              placeholder="Найти по смыслу"
              aria-label="Найти заметку по смыслу"
            />
          </form>
        )}
      </div>
      <AddNote key={view} view={view} onDone={refresh} />
      {view === 'digests' ? (
        <Digests />
      ) : (
        <>
          {q.isPending && <div className="card skeleton" style={{ height: 200 }} />}
          {q.isError && (
            <div className="card">
              <EmptyState icon={<BookOpen />} title="Не удалось загрузить">
                {(q.error as Error).message}
              </EmptyState>
            </div>
          )}
          {q.isSuccess && q.data.length === 0 && (
            <div className="card">
              <EmptyState icon={<BookOpen />} title={query ? 'Ничего не нашлось' : 'Пока пусто'}>
                {query
                  ? 'Попробуйте сказать иначе — поиск идёт по смыслу, а не по точным словам.'
                  : EMPTY[view]}
              </EmptyState>
            </div>
          )}
          {q.isSuccess && q.data.length > 0 && (
            <div className="card">
              <ul className="note-list">
                {q.data.map((n) => (
                  <li key={n.id} data-overdue={overdue(n) || undefined}>
                    <button type="button" className="note-row" onClick={() => setOpen(n.id)}>
                      <span className="note-text">{n.text}</span>
                      <NoteMeta n={n} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <NotePanel id={open} onClose={() => setOpen(null)} onChanged={refresh} />
    </div>
  );
}
